import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AuditReport - Affiche le rapport d'audit complet d'un site web
 */
const AuditReport = ({ audit, onClose, onViewRedesign }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!audit) return null;

  const { companyName, url, scores, analysis, siteData, brandColors } = audit;

  // Couleur du score basée sur la valeur
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'from-green-500 to-emerald-600';
    if (score >= 60) return 'from-yellow-500 to-amber-600';
    if (score >= 40) return 'from-orange-500 to-red-500';
    return 'from-red-500 to-rose-600';
  };

  const getImpactBadge = (impact) => {
    const colors = {
      high: 'bg-red-500/20 text-red-400 border-red-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return colors[impact] || colors.medium;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 50, scale: 0.95 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 50, scale: 0.95 }}
        className="relative w-full max-w-4xl my-8 bg-[#0a0a1a] rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 border-b border-white/10 bg-gradient-to-r from-purple-900/30 to-blue-900/30">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="flex items-center gap-4">
            {siteData?.logo && (
              <img
                src={siteData.logo}
                alt={companyName}
                className="w-12 h-12 rounded-lg object-contain bg-white p-1"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div>
              <h2 className="text-2xl font-bold text-white">{companyName}</h2>
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:underline">
                {url}
              </a>
            </div>
          </div>

          {/* Score Principal */}
          <div className="mt-6 flex items-center gap-6">
            <div className={`relative w-24 h-24 rounded-full bg-gradient-to-br ${getScoreBg(scores.seo)} p-1`}>
              <div className="w-full h-full rounded-full bg-[#0a0a1a] flex items-center justify-center">
                <span className={`text-3xl font-bold ${getScoreColor(scores.seo)}`}>{scores.seo}</span>
              </div>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Score SEO Global</p>
              <p className={`text-lg font-medium ${getScoreColor(scores.seo)}`}>
                {scores.seo >= 80 ? 'Excellent' : scores.seo >= 60 ? 'Correct' : scores.seo >= 40 ? 'À améliorer' : 'Critique'}
              </p>
              <p className="text-gray-500 text-sm mt-1">{scores.issues.length} problèmes détectés</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 bg-white/5">
          {[
            { id: 'overview', label: 'Vue d\'ensemble' },
            { id: 'issues', label: 'Problèmes' },
            { id: 'actions', label: 'Actions' },
            { id: 'impact', label: 'Impact estimé' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-purple-400 border-b-2 border-purple-400'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 max-h-[50vh] overflow-y-auto">
          <AnimatePresence mode="wait">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                {/* Summary */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h3 className="text-white font-medium mb-2">Résumé</h3>
                  <p className="text-gray-300 text-sm">{analysis?.summary}</p>
                </div>

                {/* Strengths & Weaknesses */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                    <h3 className="text-green-400 font-medium mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Points forts
                    </h3>
                    <ul className="space-y-2">
                      {analysis?.strengths?.map((strength, i) => (
                        <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                          <span className="text-green-400 mt-1">+</span>
                          {strength}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                    <h3 className="text-red-400 font-medium mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      Points faibles
                    </h3>
                    <ul className="space-y-2">
                      {analysis?.weaknesses?.map((weakness, i) => (
                        <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                          <span className="text-red-400 mt-1">-</span>
                          {weakness}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Technologies */}
                {siteData?.technologies?.length > 0 && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h3 className="text-white font-medium mb-3">Technologies détectées</h3>
                    <div className="flex flex-wrap gap-2">
                      {siteData.technologies.map((tech, i) => (
                        <span key={i} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm border border-purple-500/30">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Brand Colors */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h3 className="text-white font-medium mb-3">Palette de couleurs suggérée</h3>
                  <div className="flex gap-3">
                    {Object.entries(brandColors || {}).map(([name, color]) => (
                      <div key={name} className="text-center">
                        <div
                          className="w-12 h-12 rounded-lg border border-white/20 mb-1"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-xs text-gray-400 capitalize">{name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Issues Tab */}
            {activeTab === 'issues' && (
              <motion.div
                key="issues"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-3"
              >
                {scores?.issues?.map((issue, i) => (
                  <div key={i} className="bg-red-500/10 rounded-lg p-4 border border-red-500/20 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-200 font-medium">{issue}</p>
                      <p className="text-sm text-gray-400 mt-1">
                        {scores?.recommendations?.[i] || 'Correction recommandée'}
                      </p>
                    </div>
                  </div>
                ))}
                {(!scores?.issues || scores.issues.length === 0) && (
                  <div className="text-center py-8 text-gray-400">
                    Aucun problème majeur détecté
                  </div>
                )}
              </motion.div>
            )}

            {/* Actions Tab */}
            {activeTab === 'actions' && (
              <motion.div
                key="actions"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-3"
              >
                {analysis?.priorityActions?.map((action, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-4 border border-white/10 flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 text-purple-400 font-bold">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-200 font-medium">{action.action}</p>
                      <div className="flex gap-2 mt-2">
                        <span className={`px-2 py-0.5 rounded text-xs border ${getImpactBadge(action.impact)}`}>
                          Impact: {action.impact === 'high' ? 'Élevé' : action.impact === 'medium' ? 'Moyen' : 'Faible'}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs border ${getImpactBadge(action.effort === 'low' ? 'high' : action.effort === 'high' ? 'low' : 'medium')}`}>
                          Effort: {action.effort === 'high' ? 'Important' : action.effort === 'medium' ? 'Modéré' : 'Faible'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}

            {/* Impact Tab */}
            {activeTab === 'impact' && (
              <motion.div
                key="impact"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                <div className="text-center py-4">
                  <h3 className="text-xl font-bold text-white mb-2">Impact estimé après refonte</h3>
                  <p className="text-gray-400 text-sm">Projections basées sur des sites similaires</p>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-6 border border-blue-500/20 text-center">
                    <div className="text-3xl font-bold text-blue-400 mb-2">
                      {analysis?.estimatedImpact?.traffic || '+40%'}
                    </div>
                    <p className="text-gray-300">Trafic organique</p>
                    <p className="text-xs text-gray-500 mt-1">Visiteurs mensuels</p>
                  </div>

                  <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-6 border border-green-500/20 text-center">
                    <div className="text-3xl font-bold text-green-400 mb-2">
                      {analysis?.estimatedImpact?.conversion || '+25%'}
                    </div>
                    <p className="text-gray-300">Taux de conversion</p>
                    <p className="text-xs text-gray-500 mt-1">Visiteurs → Clients</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-6 border border-purple-500/20 text-center">
                    <div className="text-3xl font-bold text-purple-400 mb-2">
                      {analysis?.estimatedImpact?.seo || '+30 pts'}
                    </div>
                    <p className="text-gray-300">Score SEO</p>
                    <p className="text-xs text-gray-500 mt-1">Après optimisation</p>
                  </div>
                </div>

                {/* Opportunities */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h3 className="text-white font-medium mb-3">Opportunités identifiées</h3>
                  <ul className="space-y-2">
                    {analysis?.opportunities?.map((opp, i) => (
                      <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                        <span className="text-yellow-400">★</span>
                        {opp}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer CTA */}
        <div className="p-6 border-t border-white/10 bg-gradient-to-r from-purple-900/20 to-blue-900/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-white font-medium">Prêt pour la transformation ?</p>
              <p className="text-sm text-gray-400">Découvrez votre nouveau site optimisé</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-white/20 text-gray-300 rounded-lg hover:bg-white/5 transition-colors"
              >
                Fermer
              </button>
              <button
                onClick={onViewRedesign}
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Voir la proposition de refonte
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AuditReport;
